/* eslint-disable no-nested-ternary */
import React, { useCallback, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { layout, color, space, flexbox } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useSelector } from 'react-redux';
import Assets from 'theme/icons/assets';
import ContentLoader from 'theme/content-loader';
import Text from 'theme/text';
import History from 'theme/icons/history';
import ToolTip from 'theme/tooltip';
import translations from 'translations';

const Box = styled.li`
  height: 72px;
  border-bottom: 1px solid ${themeGet('colors.primary_100')};

  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  cursor: pointer;

  & > div {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    margin: 0 ${themeGet('space.s')}px;
    justify-content: center;
    align-items: flex-end;
  }

  & > div:first-child {
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex: 1;
    margin-right: 0;
  }

  :after {
    display: ${({ selected }) => (selected ? 'block' : 'none')};
    content: '';
    background: ${themeGet('colors.accent')};
    width: 96%;
    height: 2px;
    position: absolute;
    bottom: 0;
    left: 2%;
  }
`;

const StyledText = styled(Text)`
  display: flex;
  align-items: center;

  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: ${({ textTransform }) => textTransform || 'none'};
`;

const Value = styled.div`
  ${color}

  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Flex = styled.div`
  display: flex;

  ${flexbox}
  ${layout}
  ${space}
`;

const WrapIcon = styled.span`
  ${color}
  ${space}

  display: flex;
  align-items: center;
  justify-content: center;
`;

function Row({ selected, payload, onClick }) {
  const theme = useContext(ThemeContext);

  const { languageCode } = useSelector((state) => state.config);

  const handleClick = useCallback(() => {
    onClick(payload.color);
  }, [payload, onClick]);

  return (
    <Box onClick={handleClick} selected={selected}>
      <div>
        <Assets
          height="40"
          type={payload.label}
          color={themeGet(`colors.${payload.color}`)({ theme })}
        />
        <StyledText
          fontSize="h3"
          lineHeight="h3"
          fontWeight="bold"
          color="grey_600"
          textTransform="capitalize"
          ml="s"
        >
          {payload.label}
        </StyledText>
      </div>
      <div>
        <Flex flexDirection="row" pb="xxxs">
          <div>
            <Text
              fontSize="h3"
              lineHeight="h3"
              fontWeight="bold"
              color="grey_600"
              textAlign="right"
            >
              {payload.value === null ? (
                <ContentLoader
                  width={50}
                  height={16}
                  lines={[
                    {
                      x: 0,
                      y: 0,
                      rx: 5,
                      ry: 5,
                      width: 50,
                      height: 12,
                    },
                  ]}
                />
              ) : (
                payload.value
              )}
            </Text>

            <Flex
              data-tip
              data-for={`${payload.label}`}
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-end"
              width="100%"
            >
              {payload.growthRates === null ? (
                <ContentLoader
                  width={50}
                  height={10}
                  lines={[
                    {
                      x: 25,
                      y: 0,
                      rx: 5,
                      ry: 5,
                      width: 25,
                      height: 10,
                    },
                  ]}
                />
              ) : (
                <Value>
                  <Text
                    fontSize="label"
                    lineHeight="label"
                    fontWeight="bold"
                    color={
                      payload.growthRates > 0
                        ? 'lime_400'
                        : payload.growthRates < 0
                        ? 'red_400'
                        : 'grey_600'
                    }
                  >
                    {`${
                      payload.growthRates > 0 ? '+ ' : payload.growthRates < 0 ? '- ' : ''
                    }${Math.abs(payload.growthRates)}%`}
                  </Text>
                </Value>
              )}
              <WrapIcon color="primary_300" pl="xs">
                <History />
              </WrapIcon>
            </Flex>
          </div>

          <Flex minWidth="56px" justifyContent="flex-end" pl="m">
            <Text fontSize="label" lineHeight="label" fontWeight="bold" color="grey_400">
              {payload.value === null ? (
                <ContentLoader
                  width={50}
                  height={16}
                  lines={[
                    {
                      x: 25,
                      y: 0,
                      rx: 5,
                      ry: 5,
                      width: 25,
                      height: 12,
                    },
                  ]}
                />
              ) : (
                `${payload.percent}%`
              )}
            </Text>
          </Flex>
        </Flex>

        <ToolTip forData={`${payload.label}`} text={translations[languageCode].previous_period} />
      </div>
    </Box>
  );
}

export default Row;
